import type { AgentPatch } from '@tunasong/agent-tools'
import { graphHooks } from '@tunasong/graph-lib/react'
import { shortUuid } from '@tunasong/models'
import { usePlugins, useEditor, type TunaPlugin } from '@tunasong/plugin-lib'
import { startAgentTurn, useCurrentUser, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Agent, ColorName, Comment, Entity, Persisted } from '@tunasong/schemas'
import { Button, getColorCSS, MarkdownMessage } from '@tunasong/ui-lib'
import type { CSSProperties, FC, ReactNode } from 'react'
import { useRef, useState } from 'react'
import { resolveAgentVisuals } from '../agent-visuals.js'
import { commentText } from '../comment-text.js'
import { reopenExchange } from '../exchange-card/reopen-exchange.js'
import { staleProposalMessage } from '../exchange-card/stale-message.js'
import type { AnchoredExchange } from '../exchanges.js'
import { hasPersistedReply } from '../find-persisted-reply.js'
import { resolveBlockAnchorEl } from '../inline/resolve-block-anchor.js'
import { LiveAgentResponse } from '../live-agent-response.js'
import {
  acceptProposal,
  isPendingProposal,
  openDocumentReason,
  rejectProposal,
  type ProposalComment,
  type ResolveContext,
} from '../proposal-resolve.js'
import { collectDocElementIds } from '../use-anchored-exchanges.js'
import { useAgentTurnStream } from '../use-agent-turn-stream.js'
import styles from './agent-thread.module.css'
import { sectionColourOf } from './section-colour.js'
import { buildThread, mergeComments } from './thread-items.js'

export interface AgentThreadProps {
  /** A saved agent: the thread reads its comments by id */
  agent: Agent & { id: string }
  /** The document the agent is attached to */
  hostId: string
  /** Show in song is about to open a card over the song: the phone sheet collapses first (chat makeover M4) */
  onShowInSong?: () => void
  /**
   * Re-ask a failed turn. `AgentChat` passes its own, which also resets the client-tool loop it hosts; without one,
   * the thread starts the turn itself.
   */
  onRetry?: () => void
  /** Shown while the thread has nothing in it: the panel's empty state (Joni, #3070) */
  emptyState?: ReactNode
}

/**
 * T21 (spec §6, §11): a proactive review's question line. Its trigger names no section, so T20 ("Reviewed after your
 * edit to {section}") waits for a per-block trigger (§8).
 */
const reviewHeading = 'Reviewed after your recent edits'

/** The element types C7 names; anything else is a block */
const NAMED_TYPES = ['chord', 'line', 'section'] as const
const anchorLabel = (anchor: AnchoredExchange['anchor']) => {
  if (anchor?.label) {
    return `On ${anchor.label}`
  }
  const type = NAMED_TYPES.find(named => named === anchor?.elementType) ?? 'block'
  return `On this ${type}`
}

/** The first plugin whose `getProposalAriaLabel` claims the patch, so its `ProposalPreview` renders the change */
const claimPatch = (plugins: TunaPlugin[], patch?: AgentPatch) =>
  patch ? (plugins.find(plugin => Boolean(plugin.getProposalAriaLabel?.(patch))) ?? null) : null

/**
 * The Agents thread (Option A §6, chat makeover #2718): grouped by exchange, oldest first, with the live answer at the
 * bottom until its persisted reply lands. It fetches its own comments and live turn; the composer, its Stop and the
 * focus chip belong to the panel around it (Les's `ConversationPanel`).
 */
export const AgentThread: FC<AgentThreadProps> = props => {
  const { agent, hostId, onShowInSong, onRetry, emptyState } = props
  const dispatch = useThunkDispatch()
  const { userId } = useCurrentUser()
  const editor = useEditor({ allowNull: true })
  const plugins = usePlugins('all') ?? []
  const updateComment = graphHooks.useEntityUpdate<Persisted<Comment>>({})
  const updateEntity = graphHooks.useEntityUpdate<Persisted<Entity>>({})
  const { entities: fetched } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
    filter: 'comment',
  })
  // Both delivery paths: a lost WebSocket push still arrives through the fetch on remount
  const pushed = useSelector(state => state.entities.receivedEntities)
  const comments = mergeComments(agent.id, fetched, pushed)
  const items = buildThread(agent.id, comments)
  const liveStream = useAgentTurnStream(agent.id)
  const isLivePersisted = liveStream !== null && hasPersistedReply(comments, agent.id, liveStream.requestId)
  /** Exchanges whose element couldn't be found when Show in song was pressed */
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set())
  /** The exchange whose Show in song just turned into T24: its text takes the focus the button held (Joni, #3070) */
  const focusGone = useRef<string | null>(null)

  const ctx: ResolveContext = { editor, userId, updateComment, updateEntity }
  const accent = getColorCSS(resolveAgentVisuals(agent).color as ColorName)
  const docChildren = editor ? ((editor as { children?: unknown[] }).children ?? []) : null
  const docIds = docChildren ? collectDocElementIds(docChildren) : null
  /** The chip's colour: its section's, as the section block shows it; neutral outside a section (#2729) */
  const chipStyle = (exchange: AnchoredExchange): CSSProperties | undefined => {
    const colour = docChildren ? sectionColourOf(docChildren, exchange.anchor) : undefined
    return colour ? ({ '--section-color': colour } as CSSProperties) : undefined
  }

  /** Whether the exchange's element, or its line, is still in the song (§12) */
  const isInSong = (exchange: AnchoredExchange) => {
    const { anchor } = exchange
    if (!anchor || gone.has(exchange.id)) {
      return false
    }
    if (!docIds) {
      return true
    }
    return docIds.has(anchor.elementId) || Boolean(anchor.containerId && docIds.has(anchor.containerId))
  }

  const handleShowInSong = (exchange: AnchoredExchange) => {
    const element =
      resolveBlockAnchorEl(exchange.anchor?.elementId) ?? resolveBlockAnchorEl(exchange.anchor?.containerId)
    if (!element) {
      focusGone.current = exchange.id
      setGone(previous => new Set([...previous, exchange.id]))
      return
    }
    onShowInSong?.()
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    element.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
    reopenExchange(exchange, hostId, element)
  }

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
      return
    }
    if (!liveStream?.message) {
      return
    }
    startAgentTurn(dispatch, {
      agentId: agent.id,
      message: liveStream.message,
      trigger: { type: 'chat' },
      requestId: shortUuid(),
      turnId: shortUuid(),
    })
  }

  const hasLive = liveStream !== null && !isLivePersisted
  if (items.length === 0 && !hasLive && emptyState) {
    return <div className={styles.empty}>{emptyState}</div>
  }

  return (
    <div className={styles.thread} role="list">
      {items.map(item => {
        if (item.kind === 'exchange') {
          const { exchange } = item
          return (
            <section key={exchange.id} className={styles.exchange} role="listitem">
              {exchange.isReview ? (
                <p className={styles.question}>{reviewHeading}</p>
              ) : (
                <div className={styles.anchorRow}>
                  <span className={styles.anchorChip} style={chipStyle(exchange)}>
                    {anchorLabel(exchange.anchor)}
                  </span>
                  {isInSong(exchange) ? (
                    <Button variant="text" size="small" onClick={() => handleShowInSong(exchange)}>
                      Show in song
                    </Button>
                  ) : (
                    <small
                      className={styles.gone}
                      tabIndex={-1}
                      ref={element => {
                        if (element && focusGone.current === exchange.id) {
                          focusGone.current = null
                          element.focus()
                        }
                      }}
                    >
                      No longer in the song.
                    </small>
                  )}
                </div>
              )}
              {exchange.question ? <p className={styles.question}>{commentText(exchange.question)}</p> : null}
              {exchange.replies.map(reply => (
                <div key={reply.id} className={styles.answer}>
                  <MarkdownMessage content={commentText(reply)} />
                </div>
              ))}
              {exchange.proposals.map(proposal => (
                <ThreadProposal key={proposal.id} proposal={proposal} plugins={plugins} ctx={ctx} />
              ))}
            </section>
          )
        }
        if (item.kind === 'proposal') {
          return (
            <div key={item.proposal.id} role="listitem">
              <ThreadProposal proposal={item.proposal} plugins={plugins} ctx={ctx} isDecidedElsewhere />
            </div>
          )
        }
        const { comment } = item
        return (
          <div
            key={comment.id}
            role="listitem"
            className={styles.comment}
            data-author={comment.userId === 'assistant' ? 'agent' : 'user'}
          >
            <MarkdownMessage content={commentText(comment)} />
          </div>
        )
      })}
      {liveStream && hasLive ? (
        <div role="listitem">
          <LiveAgentResponse stream={liveStream} accentColor={accent} onRetry={handleRetry} />
        </div>
      ) : null}
    </div>
  )
}

interface ThreadProposalProps {
  proposal: ProposalComment
  plugins: TunaPlugin[]
  ctx: ResolveContext
  /**
   * A proposal no exchange holds is decided in the Proposals strip, so the thread shows it without a second Accept,
   * as the comment feed did
   */
  isDecidedElsewhere?: boolean
}

/** A proposal in the thread: the plugin's preview (else its summary), and Accept / Reject or the decision taken */
const ThreadProposal: FC<ThreadProposalProps> = ({ proposal, plugins, ctx, isDecidedElsewhere = false }) => {
  const [error, setError] = useState<string | null>(null)
  const patch = proposal.metadata?.patch
  const Preview = claimPatch(plugins, patch)?.components?.ProposalPreview
  const resolution = proposal.metadata?.resolution?.status

  const handleAccept = () => {
    const result = acceptProposal(proposal, ctx)
    setError(result.ok ? null : result.reason)
  }

  return (
    <div className={styles.proposal}>
      <div>{Preview && patch ? <Preview patch={patch} /> : (proposal.metadata?.summary ?? 'Proposed edit')}</div>
      {isPendingProposal(proposal) && isDecidedElsewhere ? null : isPendingProposal(proposal) ? (
        <div className={styles.actions}>
          <Button variant="contained" color="primary" size="small" onClick={handleAccept}>
            Accept
          </Button>
          <Button variant="text" size="small" onClick={() => rejectProposal(proposal, ctx)}>
            Reject
          </Button>
        </div>
      ) : (
        <small className={styles.decided}>{resolution === 'accepted' ? 'Applied.' : 'Declined.'}</small>
      )}
      {/* Other reasons are developer text (Joni, #3070): the card's stale copy says what the musician needs */}
      {error ? (
        <small className={styles.error} role="alert">
          {error === openDocumentReason ? openDocumentReason : staleProposalMessage}
        </small>
      ) : null}
    </div>
  )
}
